import { supabase } from '@/integrations/supabase/client';
import { INE_COMPLETO_TIPO_ID, INE_FRENTE_TIPO_ID, OTROS_DOCUMENTOS_TIPO_ID } from '@/utils/expediente-obligatorios';

/**
 * Subida de documentos del expediente de una PERSONA con las mismas reglas que la
 * app de cliente. Todo pasa por la Edge Function `expediente-documento`
 * (repo sozu-edge-functions), que comparte el validador con `cliente-expediente`:
 *
 * - CURP, CSF, comprobante de domicilio y actas se validan por su texto: si pasan
 *   quedan **Validados** (2) sin intervención; si están vencidos se rechazan.
 * - INE completo y pasaporte tienen revision visual: borroso, recortado, sin reverso o
 *   vencido se rechaza; si no se puede revisar queda en revisión.
 * - Lo que no se puede validar solo queda **Pendiente** (1), con el motivo.
 *
 * Antes el admin subía directo al bucket y TODO quedaba pendiente, y lo que de
 * verdad necesitaba revisión se perdía entre lo que se podía aprobar solo.
 */

export const PASAPORTE_TIPO_ID = 4;
export const INE_REVERSO_TIPO_ID = 3;
export const COMPROBANTE_DOMICILIO_TIPO_ID = 8;

/**
 * Tipos que YA NO se suben. Frente y Reverso de INE son el formato viejo (dos
 * archivos); hoy la identificación es un solo archivo: INE completo o pasaporte.
 * Se siguen LEYENDO —hay expedientes validados con ellos— pero no se ofrecen.
 */
export const TIPOS_LEGACY_SUBIDA: readonly number[] = [INE_FRENTE_TIPO_ID, INE_REVERSO_TIPO_ID];

/**
 * "Identificación oficial" genérica (53 en prod, 59 en dev): también legacy. Se
 * reconoce por nombre porque el id cambia entre ambientes.
 */
const NOMBRES_LEGACY = ['identificación oficial', 'identificacion oficial', 'frente ine', 'reverso ine'];

export function esTipoLegacySubida(tipo: { id: number; nombre?: string | null }): boolean {
  if (TIPOS_LEGACY_SUBIDA.includes(tipo.id)) return true;
  const nombre = (tipo.nombre ?? '').trim().toLowerCase();
  return NOMBRES_LEGACY.includes(nombre);
}

/** Filtra un catálogo de `tipos_documento` a lo que se puede subir hoy. */
export function tiposSubibles<T extends { id: number; nombre?: string | null }>(tipos: T[]): T[] {
  return tipos.filter((t) => !esTipoLegacySubida(t));
}

/** Las dos identificaciones que se ofrecen, como en la app. */
export const OPCIONES_IDENTIFICACION = [
  { tipoId: INE_COMPLETO_TIPO_ID, label: 'INE (frente y reverso)' },
  { tipoId: PASAPORTE_TIPO_ID, label: 'Pasaporte' },
] as const;

export function esIdentificacion(tipoId: number): boolean {
  return tipoId === INE_COMPLETO_TIPO_ID || tipoId === PASAPORTE_TIPO_ID;
}

/** Tipos que aceptan foto además de PDF (lo demás se valida por texto: solo PDF). */
const TIPOS_CON_FOTO: readonly number[] = [INE_COMPLETO_TIPO_ID, PASAPORTE_TIPO_ID, COMPROBANTE_DOMICILIO_TIPO_ID];

export function aceptaFoto(tipoId: number | null | undefined): boolean {
  return tipoId != null && TIPOS_CON_FOTO.includes(tipoId);
}

/** `accept` del input de archivo para el tipo elegido. */
export function acceptDeTipo(tipoId: number | null | undefined): string {
  return aceptaFoto(tipoId) ? '.pdf,.jpg,.jpeg,.png' : '.pdf';
}

/** Ayuda breve de qué se espera, por tipo. */
export function ayudaDeTipo(tipoId: number | null | undefined): string {
  if (tipoId === INE_COMPLETO_TIPO_ID) {
    return 'Un solo archivo con el frente y el reverso completos, nítidos y sin reflejos. PDF, JPG o PNG.';
  }
  if (tipoId === PASAPORTE_TIPO_ID) return 'La página de datos completa y nítida. PDF, JPG o PNG.';
  if (tipoId === COMPROBANTE_DOMICILIO_TIPO_ID) {
    return 'Con antigüedad máxima de 3 meses. PDF, o foto nítida (JPG/PNG).';
  }
  return 'PDF tal como se descargó (no escaneado), para validarlo automáticamente.';
}

// ── Contrato con la Edge Function ────────────────────────────────────────────

export type TipoCampo = 'texto' | 'fecha' | 'curp' | 'rfc' | 'cp' | 'sexo' | 'regimen';

export interface CampoExtraido {
  key: string;
  label: string;
  valor: string | null;
  requerido: boolean;
  tipo: TipoCampo;
  mono: boolean;
  solo_lectura: boolean;
  ayuda: string | null;
  opciones: { id: string; nombre: string }[];
}

/**
 * Dato leído que CAMBIA uno ya registrado. Al subir no se escribe directo: va a una
 * solicitud que aprueba un Super Administrador (Autorizaciones de datos). Lo que
 * llena un dato vacío sí se guarda directo.
 */
export interface CambioARevision {
  campo: string;
  etiqueta: string;
  actual: string | null;
  nuevo: string | null;
}

export interface SolicitudEnRevision {
  id: number;
  numero_ticket: number | null;
  campos: string[];
}

export interface AnalisisDocumento {
  resultado: 'valido' | 'revision' | 'rechazado';
  /** Código del rechazo (documento_vencido, documento_ilegible, ...). */
  error?: string;
  motivo: string | null;
  estatus_previsto: 'aprobado' | 'revision';
  campos: CampoExtraido[];
  hash: string;
  revisado_con_ia: boolean;
  /** Ausente si la function desplegada es anterior a 2026-10-09. */
  cambios_a_revision?: CambioARevision[];
}

export interface ResultadoSubida {
  ok: true;
  estatus: 'aprobado' | 'revision';
  motivo: string | null;
  id_documento: number;
  url: string;
  guardados: string[];
  /** Solicitud levantada con los datos que cambian algo ya registrado. */
  en_revision?: SolicitudEnRevision | null;
  /** Texto listo para quien sube. */
  aviso_revision?: string | null;
  /** El documento sí se guardó, pero el cambio no se pudo enviar (p. ej. otra pendiente). */
  revision_error?: string | null;
}

/** Error de la function con el motivo ya redactado para quien sube. */
export class ErrorDocumento extends Error {
  constructor(public codigo: string, motivo: string) {
    super(motivo);
    this.name = 'ErrorDocumento';
  }
}

const MENSAJES: Record<string, string> = {
  sin_permiso: 'No tienes permiso para subir documentos de esta persona.',
  archivo_demasiado_grande: 'El archivo pesa más de 10 MB.',
  archivo_invalido: 'No se pudo leer el archivo.',
  tipo_invalido: 'Ese tipo de documento no existe o está inactivo.',
  persona_invalida: 'La persona no existe.',
  upload_failed: 'No se pudo guardar el archivo. Intenta de nuevo.',
};

async function errorDe(err: unknown): Promise<ErrorDocumento> {
  const ctx = (err as { context?: Response } | null)?.context;
  try {
    if (ctx && typeof ctx.clone === 'function') {
      const body = (await ctx.clone().json()) as { error?: string; reason?: string } | null;
      const codigo = body?.error ?? 'error';
      return new ErrorDocumento(codigo, body?.reason ?? MENSAJES[codigo] ?? 'No se pudo procesar el documento.');
    }
  } catch {
    /* cuerpo no JSON: mensaje genérico */
  }
  return new ErrorDocumento('error', (err as Error | null)?.message ?? 'No se pudo procesar el documento.');
}

export function archivoABase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result ?? '');
      resolve(s.slice(s.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}

async function invocar<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('expediente-documento', { body });
  if (error) throw await errorDe(error);
  return data as T;
}

export interface ArchivoDePersona {
  idPersona: number;
  tipoId: number;
  file: File;
  /** base64 ya leído (lo reutiliza `subir` después de `analizar`). */
  base64?: string;
}

/** Revisa el documento SIN guardarlo: qué estatus tendría y qué datos se leyeron. */
export async function analizarDocumentoPersona(a: ArchivoDePersona): Promise<AnalisisDocumento> {
  return invocar<AnalisisDocumento>({
    action: 'analizar',
    id_persona: a.idPersona,
    tipo_id: a.tipoId,
    nombre_archivo: a.file.name,
    archivo_base64: a.base64 ?? (await archivoABase64(a.file)),
  });
}

export interface SubidaDePersona extends ArchivoDePersona {
  /** El de `analizar`: ata el archivo revisado al que se guarda. */
  hash?: string;
  /** Campos confirmados que se escriben en `personas`. Sin ellos no se toca la persona. */
  campos?: Record<string, string>;
  /**
   * `'perfil'`: la subida viene de la sección de Perfil de la persona. Es lo único que
   * actualiza datos: desde otra pantalla los `campos` se ignoran y solo se guarda el archivo.
   */
  origen?: 'perfil';
  idCuentaCobranza?: number | null;
  numero?: string | null;
  /** Solo "Otros documentos": lo que distingue un anexo de otro. */
  descripcion?: string | null;
  /** Ruta desde donde se sube: queda en la solicitud de cambio de datos. */
  vista?: string | null;
}

/** "Otros documentos" admite varios anexos vigentes: subir uno no reemplaza a los demás. */
export function esTipoAnexo(tipoId: number | null | undefined): boolean {
  return tipoId === OTROS_DOCUMENTOS_TIPO_ID;
}

/**
 * Valida y guarda. Expira la versión previa del mismo tipo (o del grupo de
 * identificación). Lanza `ErrorDocumento` si se rechaza (vencido, ilegible...).
 */
export async function subirDocumentoPersona(s: SubidaDePersona): Promise<ResultadoSubida> {
  return invocar<ResultadoSubida>({
    action: 'subir',
    id_persona: s.idPersona,
    tipo_id: s.tipoId,
    nombre_archivo: s.file.name,
    archivo_base64: s.base64 ?? (await archivoABase64(s.file)),
    hash: s.hash,
    campos: s.campos,
    origen: s.campos ? s.origen : undefined,
    id_cuenta_cobranza: s.idCuentaCobranza ?? undefined,
    numero: s.numero ?? undefined,
    descripcion: s.descripcion ?? undefined,
    vista: s.vista ?? (typeof window !== 'undefined' ? window.location.pathname : undefined),
  });
}

/**
 * Texto corto para avisar el resultado de una subida. Si los datos leídos cambiaban
 * algo ya registrado, lo dice: esos datos no se aplicaron todavía.
 */
export function avisoDeSubida(
  r: Pick<ResultadoSubida, 'estatus' | 'motivo' | 'en_revision' | 'aviso_revision' | 'revision_error'>,
): { titulo: string; detalle: string; advertencia: boolean } {
  if (r.revision_error) {
    return {
      titulo: 'Documento guardado, datos sin enviar a revisión',
      detalle: r.revision_error,
      advertencia: true,
    };
  }
  if (r.en_revision) {
    const ticket = r.en_revision.numero_ticket ? ` (ticket #${r.en_revision.numero_ticket})` : '';
    return {
      titulo: `Documento guardado; cambio de datos en revisión${ticket}`,
      detalle: r.aviso_revision ?? 'Los datos que cambian se aplicarán cuando los apruebe un Super Administrador.',
      advertencia: false,
    };
  }
  return r.estatus === 'aprobado'
    ? { titulo: 'Documento validado', detalle: 'Se revisó automáticamente y quedó validado.', advertencia: false }
    : { titulo: 'Documento en revisión', detalle: r.motivo ?? 'Quedó pendiente de revisión manual.', advertencia: false };
}
